import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  ArrowUpRight,
  CalendarDays,
  Clock3,
  Flame,
  Layers3,
  Moon,
  PieChart as PieChartIcon,
  Star,
  Target,
  TrendingUp,
  Trophy,
  UsersRound,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAthleteDashboardStore } from "@/store/api/athleteStats/useAthleteDashboardStore";
import { useAuth } from "@/contexts/AuthContext";
import { getUserProfile } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";

interface AthleteProfileSummary {
  first_name?: string | null;
}

interface BookingSummary {
  class_date: string;
  time?: string | null;
}

interface Insight {
  label: string;
  value: string | number;
  description: string;
  icon: LucideIcon;
}

export function AthleteStats() {
  const { t } = useTranslation(["athlete", "common"]);
  const { user } = useAuth();
  const [profile, setProfile] = useState<AthleteProfileSummary | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const {
    isLoading,
    averageRating,
    totalClasses,
    streak,
    totalHours,
    classTypeDistribution,
    activityData,
    bookingHistory,
    totalClassesDelta,
    firstSessionDate,
    memberSince,
    uniqueClassTypes,
    participationRate,
    cancellationRate,
    avgSessionsPerWeek,
    fetchDashboard,
  } = useAthleteDashboardStore();

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      if (!user?.id) {
        setIsProfileLoading(false);
        return;
      }
      const { data } = await getUserProfile(user.id);
      if (active) {
        setProfile(data as AthleteProfileSummary | null);
        setIsProfileLoading(false);
      }
    };
    void loadProfile();
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard]);

  const getPreferredMoment = (classes: BookingSummary[]): string => {
    const slots = { morning: 0, afternoon: 0, evening: 0 };
    classes.forEach((booking) => {
      const date = new Date(booking.class_date);
      if (booking.time) {
        const [hours, minutes] = booking.time.split(":").map(Number);
        if (Number.isFinite(hours) && Number.isFinite(minutes)) date.setHours(hours, minutes);
      }
      if (date.getHours() < 12) slots.morning += 1;
      else if (date.getHours() < 18) slots.afternoon += 1;
      else slots.evening += 1;
    });
    if (slots.morning + slots.afternoon + slots.evening === 0) return "—";
    const preferred = (Object.keys(slots) as Array<keyof typeof slots>).reduce((current, next) =>
      slots[current] > slots[next] ? current : next
    );
    return t(`stats.${preferred}`, { ns: "athlete" });
  };

  const getAverageDaysBetweenSessions = (classes: BookingSummary[]): string => {
    if (classes.length < 2) return "—";
    const dates = classes.map(({ class_date }) => new Date(class_date).getTime()).sort((a, b) => a - b);
    const intervals = dates.slice(1).map((date, index) => (date - dates[index]) / 86_400_000);
    return `${Math.round(intervals.reduce((sum, value) => sum + value, 0) / intervals.length)} ${t("stats.days", { ns: "athlete" }).toLowerCase()}`;
  };

  const insights = useMemo<Insight[]>(() => [
    { label: t("stats.estimatedCalories", { ns: "athlete" }), value: `${totalClasses * 550} kcal`, description: t("stats.estimatedCaloriesDesc", { ns: "athlete" }), icon: Flame },
    { label: t("stats.preferredMoment", { ns: "athlete" }), value: getPreferredMoment(bookingHistory as BookingSummary[]), description: t("stats.preferredMomentDesc", { ns: "athlete" }), icon: Moon },
    { label: t("stats.averageSpacing", { ns: "athlete" }), value: getAverageDaysBetweenSessions(bookingHistory as BookingSummary[]), description: t("stats.averageSpacingDesc", { ns: "athlete" }), icon: CalendarDays },
    { label: t("stats.firstSession", { ns: "athlete" }), value: firstSessionDate ? new Date(firstSessionDate).toLocaleDateString() : "—", description: t("stats.firstSessionDesc", { ns: "athlete" }), icon: CalendarDays },
    { label: t("stats.memberSince", { ns: "athlete" }), value: memberSince || "—", description: t("stats.memberSinceDesc", { ns: "athlete" }), icon: Trophy },
    { label: t("stats.diversity", { ns: "athlete" }), value: uniqueClassTypes, description: t("stats.diversityDesc", { ns: "athlete" }), icon: Layers3 },
    { label: t("stats.participationRate", { ns: "athlete" }), value: `${participationRate} %`, description: t("stats.participationRateDesc", { ns: "athlete" }), icon: UsersRound },
    { label: t("stats.cancellationRate", { ns: "athlete" }), value: `${cancellationRate} %`, description: t("stats.cancellationRateDesc", { ns: "athlete" }), icon: XCircle },
    { label: t("stats.avgSessionsPerWeek", { ns: "athlete" }), value: avgSessionsPerWeek, description: t("stats.avgSessionsPerWeekDesc", { ns: "athlete" }), icon: TrendingUp },
  ], [avgSessionsPerWeek, bookingHistory, cancellationRate, firstSessionDate, memberSince, participationRate, t, totalClasses, uniqueClassTypes]);

  if (isLoading || isProfileLoading) {
    return <div className="py-12 text-center text-muted-foreground">{t("stats.loading", { ns: "athlete" })}</div>;
  }

  const primaryStats = [
    { label: t("stats.totalClasses", { ns: "athlete" }), value: totalClasses, detail: `${totalClassesDelta > 0 ? "+" : ""}${totalClassesDelta.toFixed(1)}% ${t("stats.fromLastMonth", { ns: "athlete" })}`, icon: CalendarDays },
    { label: t("stats.averageRating", { ns: "athlete" }), value: `${averageRating}/5`, detail: t("stats.fromLastMonth", { ns: "athlete" }), icon: Star },
    { label: t("stats.streak", { ns: "athlete" }), value: streak, detail: t("stats.days", { ns: "athlete" }).toLowerCase(), icon: Trophy },
    { label: t("stats.totalHours", { ns: "athlete" }), value: totalHours, detail: t("stats.onTrack", { ns: "athlete" }), icon: Clock3 },
  ];
  const totalDistribution = classTypeDistribution.reduce((sum, item) => sum + item.value, 0);
  const chartColors = ["hsl(var(--primary))", "hsl(var(--schedule-available))", "hsl(var(--schedule-limited))", "hsl(var(--schedule-full))", "hsl(var(--muted-foreground))"];

  return (
    <div className="space-y-3 pb-20 md:space-y-4 md:pb-4">
      <section className="athlete-stats-greeting flex items-center justify-between gap-3 border border-primary/45 bg-card/80 px-4 py-3">
        <div className="min-w-0">
          <p className="font-semibold">👋 {profile?.first_name ? t("stats.helloName", { ns: "athlete", name: profile.first_name }) : t("stats.hello", { ns: "athlete" })}</p>
          <p className="text-xs text-muted-foreground">{t("stats.performanceOverview", { ns: "athlete" })}</p>
        </div>
        <Activity className="h-6 w-6 shrink-0 text-primary" />
      </section>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {primaryStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article key={stat.label} className="athlete-stat-card min-w-0 border border-border bg-card/85 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 gap-2">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold leading-tight text-muted-foreground">{stat.label}</p>
                    <p className="mt-1 text-2xl font-bold text-primary">{stat.value}</p>
                  </div>
                </div>
                <Target className="h-4 w-4 shrink-0 text-muted-foreground/40" />
              </div>
              <p className="mt-1 flex items-start gap-1 text-[9px] leading-tight text-muted-foreground"><ArrowUpRight className="h-3 w-3 shrink-0 text-primary" />{stat.detail}</p>
            </article>
          );
        })}
      </div>

      <section className="athlete-stat-card border border-border bg-card/85 p-3">
        <div className="mb-2 flex items-start gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <div><h2 className="text-sm font-semibold">{t("stats.activityOverTime", { ns: "athlete" })}</h2><p className="text-[10px] text-muted-foreground">{t("stats.activityDescription", { ns: "athlete" })}</p></div>
        </div>
        <div className="h-40 md:h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={activityData} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "hsl(var(--primary) / 0.08)" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11 }} />
              <Bar dataKey="classes" fill="hsl(var(--primary))" name={t("stats.classes", { ns: "athlete" })} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="athlete-stat-card border border-border bg-card/85 p-3">
        <div className="mb-1 flex items-start gap-2">
          <PieChartIcon className="h-5 w-5 text-primary" />
          <div><h2 className="text-sm font-semibold">{t("stats.classDistribution", { ns: "athlete" })}</h2><p className="text-[10px] text-muted-foreground">{t("stats.classDistributionDescription", { ns: "athlete" })}</p></div>
        </div>
        {classTypeDistribution.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("stats.noActivity", { ns: "athlete" })}</p>
        ) : (
          <div className="grid min-h-44 grid-cols-[minmax(0,1fr)_minmax(7rem,0.8fr)] items-center gap-2">
            <div className="h-44 min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={classTypeDistribution} dataKey="value" nameKey="name" innerRadius="32%" outerRadius="72%" stroke="hsl(var(--card))" strokeWidth={2}>{classTypeDistribution.map((item, index) => <Cell key={item.name} fill={chartColors[index % chartColors.length]} />)}</Pie><Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11 }} /></PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {classTypeDistribution.map((item, index) => <div key={item.name} className="flex items-center justify-between gap-2 text-[10px]"><span className="flex min-w-0 items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: chartColors[index % chartColors.length] }} /><span className="truncate">{item.name}</span></span><strong>{totalDistribution ? Math.round(item.value / totalDistribution * 100) : 0}%</strong></div>)}
            </div>
          </div>
        )}
      </section>

      <section className="athlete-stat-card border border-border bg-card/85 p-3">
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold"><TrendingUp className="h-5 w-5 text-primary" />{t("stats.moreInsights", { ns: "athlete" })}</h2>
        <div className="grid grid-cols-3 overflow-hidden border border-border md:grid-cols-3">
          {insights.map((insight) => {
            const Icon = insight.icon;
            return <article key={insight.label} className="min-w-0 border-b border-r border-border p-2.5 last:border-b-0"><div className="flex items-start gap-1.5"><Icon className="h-4 w-4 shrink-0 text-primary" /><div className="min-w-0"><p className="text-[9px] font-semibold leading-tight text-muted-foreground">{insight.label}</p><p className="mt-0.5 break-words text-sm font-bold text-primary">{insight.value}</p></div></div><p className="mt-1 line-clamp-2 text-[8px] leading-tight text-muted-foreground">{insight.description}</p></article>;
          })}
        </div>
      </section>
    </div>
  );
}